import { Spin, TreeSelect } from 'antd';
import clsx from 'clsx';
import {
  debounce,
  filter,
  get,
  includes,
  map,
  trim,
  uniqBy,
} from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import useLazyQueryWithCallbacks from '../common/CommonUseLazyQuery';
import {
  DROPDOWN_LIMIT,
  INITIAL_CURRENT_PAGE,
  SCROLL_CONST,
} from '../common/constants';
import usePrevious from '../common/usePrevious';

let searchDebounce = null;

const buildTreeFromFlat = (
  flatList,
  valuePath,
  titlePath,
  parentPath,
  childrenPath,
  disableCondition,
) => {
  const nodeMap = {};
  const roots = [];

  flatList?.forEach((item) => {
    const nodeValue = get(item, valuePath);
    const nodeTitle =
      typeof titlePath === 'object'
        ? map(titlePath, (p) => get(item, p))?.join(' - ')
        : get(item, titlePath);
    const isDisabled =
      typeof disableCondition === 'function' ? disableCondition(item) : false;

    nodeMap[nodeValue] = {
      key: nodeValue,
      value: nodeValue,
      title: nodeTitle,
      disabled: isDisabled,
      [childrenPath]: [],
      _raw: item,
    };
  });

  flatList?.forEach((item) => {
    const nodeValue = get(item, valuePath);
    const parentId = get(item, parentPath);
    const node = nodeMap[nodeValue];

    if (parentId && nodeMap[parentId]) {
      nodeMap[parentId][childrenPath].push(node);
    } else {
      roots.push(node);
    }
  });

  const pruneEmpty = (nodes) =>
    nodes?.map((n) => {
      const kids = n[childrenPath];
      if (kids?.length) {
        return { ...n, [childrenPath]: pruneEmpty(kids) };
      }
      const { [childrenPath]: _removed, ...rest } = n;
      return rest;
    });

  return pruneEmpty(roots);
};

const CommonTreeSelect = (props) => {
  const {
    query,
    variables = {},
    responsePath,
    uniqueByLabel = 'id',
    valuePath,
    labelPath,
    optionKey,
    callback,
    fetchPolicy,
    showSearch,
    useEffectDeps = [],
    conditionToCheckBeforeQuery = true,
    onChange = () => {},
    onTeamChange,
    currentPage: currPage = 0,
    className,
    autoFocus = false,
    customOptions = [],
    showExtraLoader = false,
    filterDataById = '',
    addExtraData,
    getPopupContainer,
    dropdownMatchSelectWidth,
    disableCondition,
    // TreeSelect-specific props
    treeCheckable = false,
    showCheckedStrategy,
    treeDefaultExpandAll = false,
    treeDataSimpleMode = false,
    parentPath = 'parentId',
    parentValuePath,
    parentLabelPath,
    parentCountPath,
    childrenPath = 'children',
    hasSelectAll,
    isSelectedAll,
    ...rest
  } = props;

  const [listData, setListData] = useState([]);
  const [currentPage, setCurrentPage] = useState(currPage);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(rest?.projectId);
  const prevRestProjectId = usePrevious(rest?.projectId);
  const [loading, setLoading] = useState(!props?.disabled);
  const selectRef = useRef(null);
  // Track teams that were selected via parent checkbox click
  const selectedTeamsRef = useRef(new Set());

  const [fetchListData] = useLazyQueryWithCallbacks(query, {
    fetchPolicy,
    onCompleted: (res) => {
      const data = get(res, responsePath, []);
      if (currentPage === 0) {
        setListData(data);
      } else {
        setListData((oldData) => uniqBy([...oldData, ...data], valuePath));
      }
      if (listData && filterDataById) {
        if (typeof filterDataById === 'string') {
          setListData((oldData) =>
            filter(oldData, (item) => item?.id !== filterDataById),
          );
        } else {
          setListData((oldData) =>
            filter(oldData, (item) => !includes(filterDataById, item?.id)),
          );
        }
      }
      if (addExtraData) {
        setListData((oldData) =>
          uniqBy([...oldData, ...addExtraData], uniqueByLabel),
        );
      }
      if (currentProjectId !== prevRestProjectId) {
        setCurrentPage(0);
        setCurrentProjectId(rest?.projectId);
      } else {
        setCurrentPage((page) => page + 1);
      }
      setHasMore(data?.length >= DROPDOWN_LIMIT);
      if (typeof callback === 'function') {
        if (addExtraData) {
          callback(res, addExtraData);
        } else {
          callback(res);
        }
      }
      setLoading(false);
    },
    onError() {
      setLoading(false);
    },
  });

  useEffect(() => {
    if (prevRestProjectId !== rest?.projectId) {
      setCurrentPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest?.projectId]);

  useEffect(() => {
    if (conditionToCheckBeforeQuery) {
      setCurrentPage(INITIAL_CURRENT_PAGE);
      fetchListData({
        fetchPolicy,
        variables: {
          ...variables,
          filter: {
            ...(variables?.filter || {}),
            skip: INITIAL_CURRENT_PAGE * DROPDOWN_LIMIT,
            limit: DROPDOWN_LIMIT,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, useEffectDeps);

  const handleScroll = (event) => {
    const { target } = event;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      scrollTop + offsetHeight >= scrollHeight - SCROLL_CONST;
    if (scrolledToBottom && hasMore && !loading) {
      fetchListData({
        variables: {
          ...variables,
          filter: {
            ...(variables?.filter || {}),
            skip: currentPage * DROPDOWN_LIMIT,
            limit: DROPDOWN_LIMIT,
            search: searchText,
          },
        },
      });
    }
  };

  const searchQuery = (search) => {
    setSearchText(trim(search));
    setCurrentPage(0);
    fetchListData({
      fetchPolicy: 'network-only',
      variables: {
        ...variables,
        filter: {
          ...(variables?.filter || {}),
          search: trim(search),
          skip: 0,
          limit: DROPDOWN_LIMIT,
        },
      },
    });
  };

  const handleSearch = (value) => {
    if (searchDebounce) {
      searchDebounce.cancel();
      searchDebounce = null;
    }
    searchDebounce = debounce(searchQuery, 500);
    searchDebounce(trim(value));
  };

  const handleClear = () => {
    if (searchText) {
      searchQuery();
    }
  };

  const buildTreeData = () => {
    const uniqueKey = parentValuePath || uniqueByLabel;
    const combined = uniqBy([...listData, ...customOptions], uniqueKey);

    if (!combined?.length) return [];

    // When parentValuePath and parentLabelPath are provided, the data is
    // already grouped (e.g. teams with nested users array). Build tree
    // with parent nodes as groups and child items as leaf nodes.
    if (parentValuePath && parentLabelPath) {
      return map(combined, (group, groupIndex) => {
        const groupValue = get(group, parentValuePath);
        const groupCount = parentCountPath ? get(group, parentCountPath) : null;
        const groupLabel = groupCount
          ? `${get(group, parentLabelPath)} - ${groupCount}`
          : get(group, parentLabelPath);
        const childItems = get(group, childrenPath, []);

        return {
          key: `${optionKey}-group-${groupValue}-${groupIndex}`,
          value: `group-${groupValue}`,
          title: groupLabel,
          selectable: false,
          children: map(childItems, (child, childIndex) => {
            const childValue = get(child, valuePath);
            const childTitle =
              typeof labelPath === 'object'
                ? map(labelPath, (p) => get(child, p))?.join(' - ')
                : get(child, labelPath);
            const isDisabled =
              typeof disableCondition === 'function'
                ? disableCondition(child)
                : false;
            return {
              key: `${optionKey}-${groupValue}-${childValue}-${childIndex}`,
              value: `${groupValue}_${childValue}`,
              title: childTitle,
              disabled: isDisabled,
              originalValue: childValue,
            };
          }),
        };
      });
    }

    if (treeDataSimpleMode) {
      return map(combined, (item) => {
        const nodeValue = get(item, valuePath);
        const nodeTitle =
          typeof labelPath === 'object'
            ? map(labelPath, (p) => get(item, p))?.join(' - ')
            : get(item, labelPath);
        const isDisabled =
          typeof disableCondition === 'function'
            ? disableCondition(item)
            : false;
        return {
          id: nodeValue,
          pId: get(item, parentPath) ?? 0,
          value: nodeValue,
          title: nodeTitle,
          disabled: isDisabled,
          key: `${optionKey}-${nodeValue}`,
        };
      });
    }

    const firstItem = combined?.[0];
    if (firstItem && get(firstItem, childrenPath) !== undefined) {
      return map(combined, (item, index) => {
        const nodeValue = get(item, valuePath);
        const nodeTitle =
          typeof labelPath === 'object'
            ? map(labelPath, (p) => get(item, p))?.join(' - ')
            : get(item, labelPath);
        const isDisabled =
          typeof disableCondition === 'function'
            ? disableCondition(item)
            : false;
        return {
          key: `${optionKey}-${nodeValue}-${index}`,
          value: nodeValue,
          title: nodeTitle,
          disabled: isDisabled,
          [childrenPath]: get(item, childrenPath),
        };
      });
    }

    return buildTreeFromFlat(
      combined,
      valuePath,
      labelPath,
      parentPath,
      childrenPath,
      disableCondition,
    );
  };

  const builtTreeData = buildTreeData();

  const treeData =
    !searchText && hasSelectAll && builtTreeData?.length
      ? [
          {
            key: `${optionKey}-select-all`,
            value: 'all',
            title: <span>{isSelectedAll ? 'Selected All' : 'Select All'}</span>,
          },
          ...builtTreeData,
        ]
      : builtTreeData;

  return (
    <div className="common-tree-select-wrapper position-relative width-percent-100">
      <Spin spinning={showExtraLoader && loading} size="small">
        <TreeSelect
          ref={selectRef}
          loading={loading}
          onPopupScroll={handleScroll}
          showSearch={
            showSearch
              ? {
                  onSearch: handleSearch,
                  filterTreeNode: (input, treeNode) => {
                    const nodeTitle = get(treeNode, 'title', '');
                    const titleStr =
                      typeof nodeTitle === 'string'
                        ? nodeTitle
                        : String(nodeTitle ?? '');
                    return titleStr
                      ?.toLowerCase()
                      ?.includes(trim(input?.toLowerCase()));
                  },
                }
              : false
          }
          onClear={handleClear}
          onBlur={handleClear}
          treeData={treeData}
          className={clsx(
            className,
            hasSelectAll && 'has-select-all',
            !searchText && loading && 'no-tag-render',
          )}
          treeDataSimpleMode={treeDataSimpleMode}
          treeCheckable={treeCheckable}
          showCheckedStrategy={showCheckedStrategy}
          treeDefaultExpandAll={treeDefaultExpandAll}
          autoFocus={autoFocus}
          popupMatchSelectWidth={dropdownMatchSelectWidth}
          getPopupContainer={(trigger) =>
            typeof getPopupContainer === 'function'
              ? getPopupContainer(trigger)
              : trigger.parentElement
          }
          onChange={(value, label, extra) => {
            let records = [];
            let finalValue = value;

            if (parentValuePath && Array.isArray(value)) {
              const allChildren = [];

              // Strip group prefix from composite values (e.g. "1971_15301" -> "15301")
              // to recover the original entity IDs
              const selectedOriginalValues = value?.map((v) => {
                const parts = String(v)?.split('_');
                return parts?.length > 1 ? parts?.slice(1)?.join('_') : v;
              });

              const teamChildMap = {};
              listData?.forEach((group) => {
                const children = get(group, childrenPath, []);
                const groupAgencyId = String(get(group, parentValuePath));
                const allChildIds = map(children, (child) =>
                  get(child, valuePath),
                );
                teamChildMap[groupAgencyId] = allChildIds;
                children?.forEach((child) => allChildren?.push(child));
              });

              // Track team selection state using original (unprefixed) values
              Object.keys(teamChildMap)?.forEach((agencyId) => {
                const childIds = teamChildMap[agencyId];
                const allSelected =
                  childIds?.length > 0 &&
                  childIds?.every((id) =>
                    includes(selectedOriginalValues, String(id)),
                  );
                const noneSelected = !childIds?.some((id) =>
                  includes(selectedOriginalValues, String(id)),
                );

                if (allSelected) {
                  selectedTeamsRef?.current?.add(agencyId);
                } else if (noneSelected) {
                  selectedTeamsRef?.current?.delete(agencyId);
                }
              });

              const excludeIds = [];
              const teamMemberIds = new Set();

              selectedTeamsRef?.current?.forEach((agencyId) => {
                const childIds = teamChildMap?.[agencyId] || [];

                childIds?.forEach((id) => {
                  teamMemberIds?.add(String(id));
                  if (!includes(selectedOriginalValues, String(id))) {
                    excludeIds?.push(id);
                  }
                });
              });

              const individualUserIds = filter(
                selectedOriginalValues,
                (id) => !teamMemberIds?.has(String(id)),
              );

              records = filter(allChildren, (item) =>
                includes(selectedOriginalValues, String(get(item, valuePath))),
              );

              // Keep composite values so TreeSelect can match them on re-render
              finalValue = value;

              // Pass team selection details to parent for payload building
              if (typeof onTeamChange === 'function') {
                onTeamChange({
                  selectedAgencyIds: [...selectedTeamsRef.current],
                  excludeIds,
                  individualUserIds,
                });
              }
            } else if (Array.isArray(value)) {
              records = filter(listData, (item) =>
                includes(value, get(item, valuePath)),
              );
            } else {
              records = filter(
                listData,
                (item) => value === get(item, valuePath),
              );
            }
            onChange(finalValue, records, label, extra);
            handleClear();
          }}
          {...rest}
        />
      </Spin>
    </div>
  );
};

export default CommonTreeSelect;
